
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import MetaTags from "@/components/MetaTags";
import CommentSection from "@/components/article/CommentSection";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/components/ui/use-toast";
import PremiumContentOverlay from "@/components/article/PremiumContentOverlay";
import ArticleStats from "@/components/article/ArticleStats";
import { ArticleStatsService } from "@/services/ArticleStatsService";
import { getArticleBySlugSync } from "@/data/articles/index";
import FeedbackSection from "@/components/article/FeedbackSection";
import { useTimedAccess } from "@/context/TimedAccessContext";
import LoadingState from "@/components/article/LoadingState";

const SUPABASE_FUNCTION_URL =
  "https://mtkkozkxmfywpibzjttu.functions.supabase.co/get-article-storage";

const ArticleYouTubeRecommendationSystem = () => {
  // Get article data from the corresponding .ts file
  const slug = "youtube-recommendation-system";
  const articleData = getArticleBySlugSync(slug);
  
  const [htmlContent, setHtmlContent] = useState<string | null>(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [initialReadCount, setInitialReadCount] = useState<number>();
  const [initialLikeCount, setInitialLikeCount] = useState<number>();

  const { isSubscribed, user, isPremiumLoading } = useAuth();
  const { hasFreeAccess } = useTimedAccess();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const isPremium = articleData?.isPremium ?? true;

  useEffect(() => {
    const loadStats = async () => {
      const stats = await ArticleStatsService.fetchStats(slug);
      setInitialReadCount(stats.readCount);
      setInitialLikeCount(stats.likeCount);
    };

    loadStats();
  }, [slug]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const preview = searchParams.get("preview") === "true" || searchParams.get("test") === "true";
    setIsPreviewMode(preview);

    if (!preview && !user) {
      navigate("/login", { state: { returnTo: location.pathname } });
      return;
    }

    if (preview && !isSubscribed) {
      toast({
        title: "Preview Mode",
        description: "You're viewing this premium article in preview mode. Subscribe for full access.",
      });
    }

    if (isSubscribed || preview || hasFreeAccess) {
      const url = `${SUPABASE_FUNCTION_URL}?slug=${slug}`;
      console.log("📦 Fetching article from:", url);
      setIsLoading(true);

      fetch(url)
        .then((res) => {
          console.log("✅ Fetch response status:", res.status);
          if (!res.ok) {
            throw new Error(`Failed to fetch article: ${res.status} ${res.statusText}`);
          }
          return res.text();
        })
        .then((html) => {
          console.log("📝 Article loaded successfully");
          setHtmlContent(html);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching article from Supabase:", error);
          setHtmlContent("<p>Sorry, the article could not be loaded at this time.</p>");
          setIsLoading(false);
        });
    }
  }, [isSubscribed, hasFreeAccess, user, navigate, location.pathname, toast]);

  const breadcrumbs = [
    { name: "Home", item: "/" },
    { name: "Learn", item: "/learn" },
    { name: "ML System Design", item: "/learn" },
    { name: articleData?.title || "YouTube Recommendation System", item: location.pathname },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <MetaTags
        title={articleData?.title || "YouTube Recommendation System"}
        description={articleData?.description || "An in-depth breakdown of YouTube's recommendation architecture and retrieval-rank loop."}
        articleData={{
          publishDate: articleData?.publishDate || "April 15, 2024",
          author: articleData?.author || "HelloML",
          category: articleData?.category || "ML System Design",
        }}
        breadcrumbs={breadcrumbs}
      />
      <Navbar />

      <main className="flex-grow py-8 md:py-12">
        <div className="container max-w-3xl">
          <div className="mb-8">
            <Link to="/learn">
              <Button variant="ghost" className="pl-0 flex items-center gap-2">
                <ArrowLeft size={16} />
                Back to resources
              </Button>
            </Link>
          </div>

          <article className="prose prose-lg max-w-none">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">
              {articleData?.title || "YouTube Recommendation System"}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-6">
              <span>{articleData?.readTime || "10 min read"}</span>
              <span>{articleData?.category || "ML System Design"}</span>
              <ArticleStats
                articleId={slug}
                initialReadCount={initialReadCount}
                initialLikeCount={initialLikeCount}
              />
            </div>

            <div className="relative">
              {isPremiumLoading && <LoadingState />}
              {!isPremiumLoading && !isSubscribed && !hasFreeAccess && !isPreviewMode && (
                <div className="mt-8 text-center">
                  <div className="p-6 bg-muted rounded-lg">
                    <h3 className="text-xl font-semibold mb-2">Premium Content</h3>
                    <p className="text-muted-foreground mb-4">
                      This article is premium content. Subscribe for full access.
                    </p>
                    <div className="flex flex-col sm:flex-row justify-center gap-4">
                      <Link to="/get-premium">
                        <Button>Get Premium Access</Button>
                      </Link>
                    </div>
                  </div>
                </div>
              )}

              {(isSubscribed || hasFreeAccess || isPreviewMode) && !isPremiumLoading && (
                isLoading ? (
                  <LoadingState />
                ) : (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: htmlContent || "<p>Sorry, the article could not be loaded.</p>",
                    }}
                  />
                )
              )}

              {isPremium && !isSubscribed && !hasFreeAccess && isPreviewMode && !isPremiumLoading && (
                <PremiumContentOverlay />
              )}
            </div>
          </article>

          <FeedbackSection articleTitle={articleData?.title || "YouTube Recommendation System"} articleId={slug} />

          {(isSubscribed || hasFreeAccess || isPreviewMode) && (
            <CommentSection articleId={slug} />
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ArticleYouTubeRecommendationSystem;
